// The global configuration singleton — GET/PUT /platform/settings, today
// the platform fee in basis points, accrued per order — beside the public
// liveness answer GET /platform/status (operational + the schema baseline
// the API runs).

import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { PlatformSettings as Settings, PlatformStatus } from './types';

export function PlatformSettings() {
  const [feeBp, setFeeBp] = useState<string>('');
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    api.get<Settings>('/platform/settings')
      .then((s) => { if (live) { setFeeBp(String(s.platformFeeBp)); setLoaded(true); } })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Failed to load.'); });
    api.get<PlatformStatus>('/platform/status')
      .then((s) => { if (live) setStatus(s); })
      .catch((e: unknown) => { if (live) setStatusError(e instanceof Error ? e.message : 'No answer.'); });
    return () => { live = false; };
  }, []);

  const bp = Number(feeBp);
  const valid = feeBp.trim() !== '' && Number.isInteger(bp) && bp >= 0 && bp <= 10000;

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      const s = await api.put<Settings>('/platform/settings', { platformFeeBp: bp });
      setFeeBp(String(s.platformFeeBp));
      setError(null);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Platform</div>
          <h1>Platform settings</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 480px) minmax(0, 360px)', gap: 20, alignItems: 'start' }}>
        <div className="panel" data-section="fees">
          <h2>Fees</h2>
          {!loaded && !error && <div className="loading">Loading…</div>}
          {loaded && (
            <form style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}
              onSubmit={(e) => { e.preventDefault(); if (valid) void save(); }}>
              <div className="field">
                <label htmlFor="platform-fee">Platform fee (basis points)</label>
                <input id="platform-fee" className="input" type="number" min={0} step={1} value={feeBp}
                  onChange={(e) => { setFeeBp(e.target.value); setSaved(false); }} />
                <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }} data-fee-percent>
                  {valid ? `= ${(bp / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}% of order value` : 'A whole number of basis points.'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !valid}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {saved && <span style={{ color: 'var(--ok)', fontSize: 13 }} role="status">Saved.</span>}
              </div>
            </form>
          )}
        </div>

        <div className="panel" data-section="status">
          <h2>Status</h2>
          {statusError && <div className="err" role="alert">{statusError}</div>}
          {!status && !statusError && <div className="loading">Asking…</div>}
          {status && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, fontSize: 13.5 }}>
              <span className="chip" data-operational={status.operational}
                style={{ '--dot': status.operational ? 'var(--ok)' : 'var(--danger)', alignSelf: 'flex-start' } as React.CSSProperties}>
                {status.operational ? 'operational' : 'not operational'}
              </span>
              <span style={{ color: 'var(--ink-2)' }}>
                Schema baseline <code className="docket" style={{ fontWeight: 500 }} data-baseline>{status.schemaBaseline.slice(0, 12) || '—'}</code>
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
